import React, { useCallback, useMemo, useState } from 'react';
import { NumberInput } from '../../../form';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import { intlFmtTemplateString } from '../../../../lib';
import type { Component } from '@formio/react';
import { isNaN } from 'lodash';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioNumberInputConstructor = {
  classNames: string;
  calculateValue: string;
  decimalLimit?: number;
};
/**
 * @param classNames a string separated by spaces that will be added as classNames
 */
export type EhsFormioNumberInputSettings = {
  classNames?: string;
  calculateValue?: string;
  decimalPlaces: number;
};
export const EhsFormioNumberInputType = 'ehsNumberInput';
export class EhsFormioNumberInput extends EhsFormioReactComponent<
  EhsFormioNumberInputSettings,
  EhsFormioNumberInputConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioNumberInputType,
      label: 'common.unknown',
      key: EhsFormioNumberInputType,
      classNames: '',
      calculateValue: '',
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-NumberInput',
      group: 'advanced',
      icon: 'hashtag',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioNumberInput.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        },
        {
          type: 'textarea',
          input: true,
          key: 'calculateValue',
          label: 'Calculate values',
          editor: 'ace',
          as: 'javascript',
          rows: 3,
          weight: 30,
          tooltip:
            'Enter custom javascript code, Example: value = data.a + data.b + data.c; [Available Variables: token, form, submission, data, row, component, instance, value, moment, _, utils, util]'
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private calculateValue = '';
  private decimalPlaces = 0;
  extractFields(component: Component<EhsFormioNumberInputConstructor>): void {
    this.classNames = component.classNames;
    this.calculateValue = component.calculateValue;
    this.shouldSetValue = true;
    if (!component.calculateValue) {
      this.dataForSetting = this._data[component.key as string];
    }
    this.decimalPlaces = component.decimalLimit ?? 0;
  }

  attachEhsReact(): [
    (
      props: EhsFormioReactProps<EhsFormioNumberInputSettings>
    ) => React.JSX.Element,
    EhsFormioNumberInputSettings
  ] {
    return [
      NumberInputCmp,
      {
        classNames: this.classNames,
        calculateValue: this.calculateValue,
        decimalPlaces: this.decimalPlaces
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link EhsFormioNumberInputSettings} properties
 */
function NumberInputCmp({
  value,
  onChange,
  label,
  readOnly,
  required,
  defaultValue,
  settings,
  keyForComponent
}: EhsFormioReactProps<EhsFormioNumberInputSettings>): React.JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );

  const maxNumber = 1000000;
  const [errorString, setErrorString] = useState('');
  const onChangeWrapper = useCallback(
    (val: number | undefined): void => {
      if (val === undefined || val === null) {
        setErrorString('');
        onChange('');
        return;
      }

      if (isQuestionnaire) {
        onChange(formatNumberToDecimalPlaces(val, settings.decimalPlaces));
        return;
      }

      // These checks are only for the formio component used within Stroke.
      if (val <= maxNumber) {
        setErrorString('');
        onChange(formatNumberToDecimalPlaces(val, settings.decimalPlaces));
      } else {
        setErrorString(
          intlFmtTemplateString('react.field.number.max', {
            max: maxNumber.toString()
          })
        );
      }
    },
    [isQuestionnaire, onChange, settings.decimalPlaces]
  );
  const wrapperValue = useMemo(() => {
    if (value == '') {
      return undefined;
    }
    return (
      (value == null || !value || isNaN(value) ? undefined : Number(value)) ??
      (defaultValue == null || isNaN(defaultValue)
        ? undefined
        : Number(defaultValue))
    );
  }, [defaultValue, value]);

  return useMemo(() => {
    return (
      <NumberInput
        decimalPlaces={settings.decimalPlaces}
        onChange={onChangeWrapper}
        value={wrapperValue}
        label={label}
        readOnly={!!(readOnly || settings.calculateValue)}
        className={settings.classNames}
        error={errorString || errorMessage}
        required={required}
        labelOnTop={isQuestionnaire}
      />
    );
  }, [
    settings.decimalPlaces,
    settings.calculateValue,
    settings.classNames,
    onChangeWrapper,
    wrapperValue,
    label,
    readOnly,
    errorString,
    errorMessage,
    required,
    isQuestionnaire
  ]);
}

export function formatNumberToDecimalPlaces(
  val: number | string,
  decimalPlaces: number
): string {
  if ((typeof val === 'string' && val.trim() === '') || isNaN(Number(val))) {
    return '';
  }
  if (typeof val === 'string') {
    val = Number(val);
  }

  return decimalPlaces > 0 ? val.toFixed(decimalPlaces) : String(val);
}
